import { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface Announcement {
  id: number;
  title: string;
  content: string;
  type: 'info' | 'success' | 'warning' | 'danger' | 'feature' | 'maintenance';
  target_roles: string[] | null;
  is_active: boolean;
  priority: number;
  show_on_dashboard: boolean;
  show_as_popup: boolean;
  icon: string;
  created_by_name: string;
  is_read?: boolean;
  is_dismissed?: boolean;
  read_at?: string | null;
  dismissed_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface AnnouncementHook {
  announcements: Announcement[];
  loading: boolean;
  error: string | null;
  markAsRead: (announcementId: number) => Promise<void>;
  markAsDismissed: (announcementId: number) => Promise<void>;
  refreshAnnouncements: () => Promise<void>;
}

export const useAnnouncements = (): AnnouncementHook => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.get('/announcements/dashboard');
      // Dashboard endpoint direkt array döndürüyor
      setAnnouncements(response || []);
    } catch (error: any) {
      console.error('Error fetching announcements:', error);
      setError(error.response?.data?.error || 'Duyurular yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (announcementId: number) => {
    try {
      await apiService.post(`/announcements/${announcementId}/mark`, { action: 'read' });
      
      // Update local state
      setAnnouncements(prev => 
        prev.map(announcement => 
          announcement.id === announcementId 
            ? { ...announcement, is_read: true, read_at: new Date().toISOString() }
            : announcement
        )
      );
    } catch (error: any) {
      console.error('Error marking announcement as read:', error);
      throw error;
    }
  };

  const markAsDismissed = async (announcementId: number) => {
    try {
      await apiService.post(`/announcements/${announcementId}/mark`, { action: 'dismiss' });
      
      // Remove from local state
      setAnnouncements(prev => 
        prev.filter(announcement => announcement.id !== announcementId)
      );
    } catch (error: any) {
      console.error('Error dismissing announcement:', error);
      throw error;
    }
  };

  const refreshAnnouncements = async () => {
    await fetchAnnouncements();
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  return {
    announcements,
    loading,
    error,
    markAsRead,
    markAsDismissed,
    refreshAnnouncements
  };
};